// frontend/src/components/homepage/artefacts/MultiChips.js
//
// Tile 05's artefact: four profession chips popping in (`hl-chipPop`, .15s
// apart) over a dashed row of areas - and then CYCLING, one chip at a time,
// through the same profession deck the hero's ignite tags draw from, so the
// tile shows the breadth of what the product matches instead of four fixed
// names. The chip's dot colour is a custom property on the chip
// (`--hl-ft-dot`), the prototype's `--dot`; the dot stays with its SLOT, so
// the four-colour rotation never changes.
//
// CHOREOGRAPHY (owner round 2): every SWAP_EVERY_MS one slot fades + scales
// out (SWAP_OUT_MS), the next label fades in in its place, and the next slot
// is only touched SWAP_EVERY_MS later - never two at once. Labels come off a
// shuffled deck dealt to exhaustion (`createFieldDeck`) driven by a SEEDED
// generator, so the sequence is the same on every load and a test can assert
// it; a dealt label that is currently on screen is skipped, so a name never
// reappears while it is still visible, and never twice in a row.
//
// WHEN IT DOES NOT RUN: motion off and the prerenderer both render the four
// static chips with no timer armed (the snapshot and the "stop animations"
// visitor see the prototype's tile); off-screen and hidden-tab pause the
// chain and resume where it stopped. A slot caught mid-swap when the chain
// pauses is settled back to visible with its old label, so nothing is ever
// left faded out.
//
// PROFESSION PAGES (optional props): `labels` replaces the four opening chips
// and `pool` the cycle's deck, so /lawyers shows lawyer specialties rather than
// the homepage's cross-trade mix. The cycle only runs when the pool holds MORE
// labels than there are chips - a pool no larger than the chip row would deal a
// label already on screen, so such a page keeps its four chips still. The
// homepage passes nothing and is unchanged.
//
// Memo'd like every other artefact.

import React, { memo, useEffect, useRef, useState } from "react";
import useInViewGate, { isPrerendering } from "../../../motion/useInViewGate";
import useMotion from "../../../motion/useMotion";
import { createFieldDeck } from "../../../homepage/fieldDeck";
import { FIELDS } from "../heroData";

/** The four chips the tile opens with (and shows under motion-off). */
export const CHIPS = [
  { label: "עורך דין", dot: "#00A896" },
  { label: "ייעוץ עסקי", dot: "#5AB5E3" },
  { label: "צלם אירועים", dot: "#E58C98" },
  { label: "מכון קוסמטיקה", dot: "#E8907A" },
];

export const AREAS = ["תל אביב", "רעננה", "ירושלים", "+"];

/**
 * The six professions the owner named for this tile. All six already sit in
 * the hero's FIELDS (heroData.js), so this list is the tile's own statement
 * of the request and the union below de-duplicates them; it is NOT a second
 * deck. Add a NEW profession to FIELDS, not here.
 */
export const OWNER_FIELDS = [
  "שיווק דיגיטלי",
  "סוכן ביטוח",
  "עיצוב פנים",
  "רואה חשבון",
  "פיתוח אתרים",
  "אילוף כלבים",
];

/** Every label the cycle can deal: the opening four + the hero deck, once each. */
export const CYCLE_POOL = Array.from(
  new Set([...CHIPS.map((chip) => chip.label), ...FIELDS, ...OWNER_FIELDS])
);

/** One slot swaps every 2.2s; the outgoing chip takes 300ms of that. */
export const SWAP_EVERY_MS = 2200;
export const SWAP_OUT_MS = 300;

/** Which slot swaps next, in turn - a fixed, non-sequential order so the
 *  tile does not tick around the grid like a clock. */
export const SLOT_ORDER = [0, 3, 1, 2];

/** The generator seed - any change reorders the sequence on every load. */
export const DECK_SEED = 0x5eed05;

export const OUT_CLASS = "hl-ft-chip-out";
export const IN_CLASS = "hl-ft-chip-in";

/**
 * mulberry32 - a 32-bit seeded generator, good enough to shuffle a deck of
 * thirty labels the same way every time. Returns a `Math.random`-shaped
 * function so `createFieldDeck` takes it as is.
 *
 * @param {number} seed
 * @returns {() => number}
 */
export function seededRandom(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * The label dealer: a seeded deck over `pool` that never deals a label
 * currently on screen. Bounded - a deck of n labels with k visible skips at
 * most k per call, and a pool no larger than the visible set falls back to the
 * deck's own order rather than spinning.
 *
 * @param {string[]} pool
 * @param {number} [seed]
 * @returns {{ next: (visible: string[]) => string }}
 */
export function createChipCycle(pool, seed = DECK_SEED) {
  const deck = createFieldDeck(pool, seededRandom(seed));
  return {
    next(visible) {
      let label = deck.next();
      let guard = pool.length;
      while (guard > 0 && visible.includes(label) && pool.length > visible.length) {
        label = deck.next();
        guard -= 1;
      }
      return label;
    },
  };
}

const DEFAULT_LABELS = CHIPS.map((chip) => chip.label);

const toChips = (labels) => labels.map((label) => ({ label, anim: "" }));

function MultiChips({ labels = DEFAULT_LABELS, pool = CYCLE_POOL }) {
  const initialChips = () => toChips(labels);
  const { off } = useMotion();
  const [gateRef, inView] = useInViewGate({ threshold: 0.2 });
  const [chips, setChips] = useState(initialChips);
  const [visible, setVisible] = useState(
    () => typeof document === "undefined" || document.visibilityState !== "hidden"
  );
  const cycleRef = useRef(null);
  const turnRef = useRef(0);

  const still = off || isPrerendering() || pool.length <= labels.length;
  const active = !still && inView && visible;

  useEffect(() => {
    const onVisibility = () => setVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  useEffect(() => {
    if (!active) {
      // Settle any slot caught mid-swap: keep its old label, make it visible.
      setChips((current) =>
        current.some((chip) => chip.anim === OUT_CLASS)
          ? current.map((chip) => (chip.anim === OUT_CLASS ? { ...chip, anim: "" } : chip))
          : current
      );
      return undefined;
    }

    if (!cycleRef.current) cycleRef.current = createChipCycle(pool);
    const cycle = cycleRef.current;
    let timer = null;

    const swapIn = (slot) => {
      setChips((current) => {
        const next = cycle.next(current.map((chip) => chip.label));
        return current.map((chip, i) => (i === slot ? { label: next, anim: IN_CLASS } : chip));
      });
      timer = setTimeout(swapOut, SWAP_EVERY_MS - SWAP_OUT_MS);
    };

    const swapOut = () => {
      const slot = SLOT_ORDER[turnRef.current % SLOT_ORDER.length];
      turnRef.current += 1;
      setChips((current) =>
        current.map((chip, i) => (i === slot ? { ...chip, anim: OUT_CLASS } : chip))
      );
      timer = setTimeout(() => swapIn(slot), SWAP_OUT_MS);
    };

    timer = setTimeout(swapOut, SWAP_EVERY_MS);
    return () => clearTimeout(timer);
    // `pool` is a page constant (a content module's array); it never changes
    // while the tile is mounted, so it is not a restart trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Motion off / prerender: the prototype's four chips, exactly.
  const shown = still ? initialChips() : chips;

  return (
    <>
      <div ref={gateRef} className="hl-ft-chips" aria-hidden="true">
        {shown.map((chip, i) => (
          <span
            key={CHIPS[i].dot}
            className={chip.anim || undefined}
            style={{ "--hl-ft-dot": CHIPS[i].dot }}
          >
            {chip.label}
          </span>
        ))}
      </div>
      <div className="hl-ft-areas" aria-hidden="true">
        {AREAS.map((area) => (
          <span key={area}>{area}</span>
        ))}
      </div>
    </>
  );
}

export default memo(MultiChips);
